<script setup lang="ts">
import MapBoxMap from '@/@web/ui/components/map/MapBoxMap.vue'
import { onBeforeUnmount, onMounted, provide, watch } from 'vue'
import { ExclusionZoneViewModel } from '../viewmodels/ExclusionZone.viewmodel'
import { ExclusionZoneRepositoryApiAdapter } from '@/@core/exclusion-zone/infra/ExclusionZoneRepositoryApi.adapter'
import { MonitoredAreaRepositoryApiAdapter } from '@/@core/monitored-area/infra/monitored-area-repository-adapter/MonitoredAreaRepositoryApi.adapter'
import { fireRiskApi } from '@/services/clients/AxiosClients'
import { feature, featureCollection } from '@turf/turf'
import Button from '@/components/ui/button/Button.vue'
import { FeatureCollection, Geometry } from 'geojson'
import MapBoxZoomControl from '@/@web/ui/components/map/MapBoxZoomControl.vue'
import MapBoxDrawPolygonControl from '@/@web/ui/components/map/MapBoxDrawPolygonControl.vue'
import MapBoxDrawDeleteControl from '@/@web/ui/components/map/MapBoxDrawDeleteControl.vue'
import { MapFillLayerFactory } from '../../../ui/components/map/factories/MapFillLayer.factory'
import { MapOutlineLayerFactory } from '../../../ui/components/map/factories/MapOutlineLayer.factory'
import MapBoxLayer from '../../../ui/components/map/MapBoxLayer.vue'
import MapBoxSource from '../../../ui/components/map/MapBoxSource.vue'
import MapLayout from '../../@shared/layouts/Map.layout.vue'

const repository = new ExclusionZoneRepositoryApiAdapter(fireRiskApi)
const monitoredAreaRepository = new MonitoredAreaRepositoryApiAdapter(fireRiskApi)

const vm = new ExclusionZoneViewModel(repository, monitoredAreaRepository)
provide('exclusion-zone-view-model', vm)

onMounted(() => {
	if (!vm.exclusionZone.value.description) {
		vm.goToExclusionZonePage()
	}
})

watch(
	() => vm.drawnFeatures.value,
	() => vm.fitBounds(),
	{ deep: true },
)

watch(
	() => vm.exclusionZone.value.monitoredAreaId,
	() => vm.fitBounds(),
)

watch(
	() => vm.exclusionZone.value.zones,
	(value) => {
		if (value.length) {
			const features = value.map((zone: Geometry) =>
				Object.assign(feature(zone), {
					properties: {
						cor: '#F82027',
					},
				}),
			)
			const geojson = featureCollection(features) as FeatureCollection

			vm.drawnFeatures.value = geojson
		}
	},
	{
		immediate: true,
	},
)

onBeforeUnmount(() => vm.resetState())
</script>

<template>
	<div class="w-full h-full relative">
		<map-layout>
			<div class="!w-[500px] top-0 bottom-0 left-0 absolute z-10 bg-white-neutral h-full !p-8">
				<div class="flex flex-col gap-2">
					<h2 class="text-H5">Definição da Área</h2>
					<p class="text-default">
						Utilize a ferramenta de desenho livre ao lado para criar a zona de exclusão. Para
						finalizar a forma basta clicar duas vezes.
					</p>
				</div>

				<div class="flex justify-between gap-4 mt-8">
					<Button
						text="cancelar"
						secondary
						:disabled="vm.fetching.value"
						@click="vm.goToExclusionZonePage()"
						class="w-1/2 justify-center"
					/>
					<Button
						v-if="vm.exclusionZone.value.id"
						text="atualizar"
						:loading="vm.fetching.value"
						:disabled="!vm.isFormValid.value || !vm.drawnFeatures.value?.features.length"
						@click="() => vm.updateExclusionZone(vm.drawnFeatures.value)"
						class="w-1/2 justify-center"
					/>
					<Button
						v-else
						text="salvar"
						:loading="vm.fetching.value"
						:disabled="!vm.isFormValid.value || !vm.drawnFeatures.value?.features.length"
						@click="() => vm.createExclusionZone(vm.drawnFeatures.value)"
						class="w-1/2 justify-center"
					/>
				</div>
			</div>

			<map-box-map
				draw
				@load="vm.setMapListeners($event)"
			>
				<map-box-source
					v-if="vm.mapInstance.value"
					id="shape"
					:spec="{
						type: 'geojson',
						data: vm.geojson.value,
					}"
				/>

				<map-box-layer
					v-if="vm.mapInstance.value"
					:spec="MapFillLayerFactory.make()"
				/>
				<map-box-layer
					v-if="vm.mapInstance.value"
					:spec="MapOutlineLayerFactory.make()"
				/>

				<map-box-zoom-control v-if="vm.mapInstance.value" />
				<map-box-draw-polygon-control v-if="vm.mapInstance.value" />
				<map-box-draw-delete-control v-if="vm.mapInstance.value" />
			</map-box-map>
		</map-layout>
	</div>
</template>

<style scoped>
.mapboxgl-map {
	height: 100%;
	width: 100%;
}
</style>
